'use client';

import dynamic from 'next/dynamic';
import { createContext, useContext } from 'react';
import { Markdown } from '../markdown';
import type { ProseMode } from './prose-markdown';
import type { SearchReferenceItems } from './prose-references';
import styles from './prose-editor.module.css';

export type { ProseMode } from './prose-markdown';

export interface ProseEditorProps {
  /** The stored markdown the editor opens on. */
  value: string;
  /** Fired once, on blur, when the written markdown differs from what the
   *  field held: the new text, or null when the person emptied it. */
  onCommit: (markdown: string | null) => void;
  /** Fired on Escape, and on a blur that changed nothing: the field closes
   *  the editor and keeps its text. */
  onCancel: () => void;
  /** Names the editable region for assistive tech (the field's label). */
  ariaLabel: string;
  placeholder?: string;
  /** `block` for a description or a module's prose field, `inline` for the
   *  note (marks, links and line breaks; no block). */
  mode?: ProseMode;
  /** The most characters the written markdown may hold (the note's limit);
   *  an edit past it is refused. */
  maxLength?: number;
  /** A commit is in flight: the text cannot change. */
  disabled?: boolean;
  /** Focus the editor when it mounts: a person opened it to write. */
  autoFocus?: boolean;
  /** Where the click that opened the editor landed, relative to the top-left
   *  corner of the rest box, so the caret lands there and not at the end. */
  caretAt?: { x: number; y: number };
  /** The written markdown after every change (the note's length counter). */
  onChange?: (markdown: string) => void;
  /** The box: the host's padding and margins, the ones its rest box wears. */
  className?: string;
  /** The text: the host's type, the class its rendered prose wears. */
  bodyClassName?: string;
  /** The person refs that can open the item the field belongs to: a person
   *  reference outside them wears the quiet "cannot see this item" note
   *  (docs/PROSE.md §5.4). Omitted where the field has no item (a group's
   *  description) or the host has none to hand: no note is shown. */
  audience?: readonly string[];
  /** The `#` list's search (the server action `searchReferenceItemsAction`):
   *  without it, the editor offers no item reference. */
  searchItems?: SearchReferenceItems;
}

/** The props of the editor being loaded, for the frame that stands in. */
const Loading = createContext<ProseEditorProps | null>(null);

/** While the editor's code loads, the box with the strip's row and the
 *  rendered text, so the box keeps its size and place when the editor comes. */
function LoadingFrame() {
  const props = useContext(Loading);
  if (!props) return null;
  return (
    <div className={[styles.box, props.className].filter(Boolean).join(' ')} aria-busy="true">
      <div className={styles.content}>
        <Markdown
          text={props.value}
          inline={props.mode === 'inline'}
          className={props.bodyClassName}
        />
      </div>
      <div className={styles.strip} />
    </div>
  );
}

const Body = dynamic(() => import('./prose-editor-body').then((m) => m.ProseEditorBody), {
  ssr: false,
  loading: LoadingFrame,
});

/**
 * **The prose editor** (docs/PROSE.md §4): a WYSIWYG editor on ProseMirror
 * through Tiptap core for every prose field (the description, a module's
 * prose field, the note). The stored truth stays markdown: the editor opens on
 * the field's markdown and hands markdown back (`prose-markdown.ts`).
 *
 * A person formats with the strip at the top of the box (bold, italic,
 * strikethrough, code, a bullet or numbered list, a heading, a quote, a link,
 * a reference to a person or an item)
 * or with the keyboard (Mod-B, Mod-I, Mod-Shift-S, Mod-E, Mod-Shift-8,
 * Mod-Shift-7, Mod-K for a link, and markdown typed at a line's start:
 * `- `, `1. `, `# `, `> `, ```` ``` ````, `---`). Escape reverts and closes; a
 * blur outside the box commits when the text changed.
 *
 * The editor's code is loaded when a person first opens a field to edit it
 * (`next/dynamic`), so a page at rest carries none of it.
 */
export function ProseEditor(props: ProseEditorProps) {
  return (
    <Loading.Provider value={props}>
      <Body {...props} />
    </Loading.Provider>
  );
}
